import {
  buildDisplayLabel,
  getProcurementRowIdentity,
  normalizeText,
} from "./procurementHelpers";

export const EMPTY_WORKFLOW_LOCK_STATE = {
  isLocked: false,
  predecessorIds: [],
  successorIds: [],
  unresolvedPredecessors: [],
};

function getDependencyLookupId(value) {
  const normalizedValue = normalizeText(value);
  return normalizedValue ? normalizedValue : "";
}

function hasCompletedActualDelivery(row) {
  return Boolean(normalizeText(row?.actualDeliveryDate));
}

function buildDependencyItemLabel(dependencyItem, row, dependencyNode) {
  return buildDisplayLabel(
    normalizeText(
      dependencyItem?.specNumber ||
        row?.specNumber ||
        dependencyNode?.info?.specNumber,
    ),
    normalizeText(
      dependencyItem?.specTitle ||
        row?.specTitle ||
        dependencyNode?.info?.specTitle,
    ),
  );
}

function isDependencyCompleted(entryId, rowById, dependenciesDict) {
  const liveRow = rowById.get(entryId);

  if (liveRow) {
    return hasCompletedActualDelivery(liveRow);
  }

  const storedDependency = dependenciesDict?.[entryId];
  return Boolean(storedDependency?.info?.completed);
}

export function buildDependencyStateByEntryId(dependenciesDict, rowsData) {
  const safeDependenciesDict = dependenciesDict || {};
  const rowById = new Map(
    (rowsData || [])
      .map((row) => [getProcurementRowIdentity(row), row])
      .filter(([entryId]) => Boolean(entryId)),
  );
  const allEntryIds = new Set([
    ...Object.keys(safeDependenciesDict),
    ...rowById.keys(),
  ]);
  const dependencyStateByEntryId = {};

  allEntryIds.forEach((entryId) => {
    const node = safeDependenciesDict?.[entryId] || {};
    const predecessorItems = Array.isArray(node?.predecessors)
      ? node.predecessors
      : [];
    const successorItems = Array.isArray(node?.successors)
      ? node.successors
      : [];
    const unresolvedPredecessors = predecessorItems
      .map((predecessor) => {
        const predecessorId = getDependencyLookupId(predecessor?.id);

        if (!predecessorId) {
          return null;
        }

        if (
          isDependencyCompleted(predecessorId, rowById, safeDependenciesDict)
        ) {
          return null;
        }

        return {
          id: predecessorId,
          label: buildDependencyItemLabel(
            predecessor,
            rowById.get(predecessorId),
            safeDependenciesDict?.[predecessorId],
          ),
        };
      })
      .filter(Boolean);

    dependencyStateByEntryId[entryId] = {
      isLocked: unresolvedPredecessors.length > 0,
      predecessorIds: predecessorItems
        .map((predecessor) => getDependencyLookupId(predecessor?.id))
        .filter(Boolean),
      successorIds: successorItems
        .map((successor) => getDependencyLookupId(successor?.id))
        .filter(Boolean),
      unresolvedPredecessors,
    };
  });

  return dependencyStateByEntryId;
}

export function getWorkflowLockStateForRow(dependencyStateByEntryId, row) {
  const entryId = getProcurementRowIdentity(row);

  if (!entryId) {
    return EMPTY_WORKFLOW_LOCK_STATE;
  }

  return dependencyStateByEntryId?.[entryId] || EMPTY_WORKFLOW_LOCK_STATE;
}

export function getWorkflowLockTooltipLabels(lockState) {
  return (lockState?.unresolvedPredecessors || [])
    .map((predecessor) => normalizeText(predecessor?.label))
    .filter(Boolean);
}

export function buildWorkflowLockTooltip(lockState) {
  if (!lockState?.isLocked) {
    return "";
  }

  const labels = getWorkflowLockTooltipLabels(lockState);

  if (!labels.length) {
    return "Blocked until these predecessor submittals are completed:";
  }

  if (labels.length === 1) {
    return `Blocked by predecessor: ${labels[0]}`;
  }

  if (labels.length <= 3) {
    return `Blocked by predecessors: ${labels.join(", ")}`;
  }

  return `Blocked by predecessors: ${labels
    .slice(0, 3)
    .join(", ")}, and ${labels.length - 3} more`;
}
